<template>
  <div class="matter-details">

    <SkeletonText :loading="loading">

      <div v-if="dontShowMatterFields"
           class="empty-state empty-matter-fields">
        <div class="empty-state-title empty-state-content">
          {{$t('client.matters.matter_page.no_additional_info')}}
        </div>
        <FrontageLink to="app.client_card_settings"
                      :params="{origin: 'Matter info empty state cta'}">
          <div class="empty-state-cta empty-state-content">
            <v-icon>icon-Plus</v-icon>
            {{$t('client.add_fields')}}
          </div>
        </FrontageLink>
      </div>

      <MatterFields v-else
                    :matter-fields="fields"/>

      <hr class="separator">

      <MatterTags :tags="matter.tags || []"
                  @edit-fields="editFields"/>

      <hr v-if="isMultiStaff"
          class="separator">

      <MatterStaff v-if="isMultiStaff"
                   :matter="matter"
                   :all-staff="staffMembers"
                   :collaborators="collaborators"
                   @update-collaborators="refreshCollaborators({clientUid: matterContact.uid})"
                   @change-staff="changeStaff"/>

    </SkeletonText>

  </div>
</template>

<script>
  import MatterFields from "./matterDetails/MatterFields";
  import MatterTags from "./matterDetails/MatterTags";
  import MatterStaff from "./matterDetails/MatterStaff";
  import {SkeletonText, FrontageLink} from '@/modules/common/components';
  import {mapGetters} from "vuex";

  export default {
    name: "MatterDetails",
    components: { MatterFields, MatterTags, MatterStaff, SkeletonText, FrontageLink },
    props: {
      matter: {
        type: Object,
        required: true
      }
    },
    data() {
      return {
        loading: true
      }
    },
    async created() {
      this.$frontageApi.listen('fetch-new-messages', this.refreshCollaborators);

      await Promise.all([
        this.fetchFields(),
        this.fetchCollaborators()
      ]);

      this.loading = false;
    },
    computed: {
      ...mapGetters("clientFields", ["matterFields", "deletedMatterFields"]),
      ...mapGetters("accountModule", ["staffMembers"]),
      ...mapGetters('mattersModule', ['collaborators']),
      fields() {
        const fields = this.matterFields.map((field) => {
          const matterField = this.matter.fields.find(matterField => matterField.uid === field.id);
          let value = matterField && matterField.value || null;
          if(field.type === 'checkbox') {
            // in case not mark will be undefined and will not show text.
            // in case of false will show the correct "No" text
            value = matterField && matterField.value;
          }
          return {
            label: field.label,
            type: field.type,
            value
          };
        });

        this.deletedMatterFields.forEach((field) => {
          const deletedField = this.matter.fields.find(matterField => matterField.uid === field.id);
          deletedField && deletedField.value && fields.push({
            label: field.label,
            type: field.type,
            value: deletedField.value
          });
        });

        return fields.filter(field => field.type !== 'name');
      },
      matterContact() {
        return this.matter.contacts[0];
      },
      isMultiStaff() {
        const assignedStaff = this.staffMembers.find(staff => staff.uid === this.matter.staff.uid);
        return this.staffMembers.filter(member => member.active).length > 1 || (assignedStaff && !assignedStaff.active);
      },
      dontShowMatterFields() {
        return !this.matterFields.length || (this.matterFields.length === 1 && this.matterFields[0].type === 'name');
      }
    },
    methods: {
      async fetchCollaborators() {
        await this.$store.dispatch({type: "mattersModule/fetchCollaborators"});
      },
      async fetchFields() {
        await this.$store.dispatch({type: "clientFields/fetchClientFields"});
      },
      editFields() {
        this.$frontageApi.send('matter_action', {action: 'edit_matter', uid: this.matter.uid});
      },
      changeStaff() {
        this.$frontageApi.send('matter_action', {action: 'change_staff', matter: this.matter, staffUid: this.matter.staff.uid});
      },
      async refreshCollaborators({clientUid}) {
        if (this.matterContact.uid === clientUid) {
          await this.$store.dispatch({type: "mattersModule/refreshCollaborators"});
          this.$frontageApi.send('matter_action', {action: 'refresh_collaborators'});
        }
      }
    },
    destroyed() {
      this.$frontageApi.remove('fetch-new-messages', this.refreshCollaborators);
    }
  }
</script>

<style lang="scss" scoped>
  @import "@/assets/scss/abstract/_variables.scss";

  .matter-details {

    .separator {
      border: none;
      height: 1px;
      background-color: $grey-color-2;
      margin: 15px 0 20px;
    }
  }
</style>
